/**
 * Pure reducers for the Risk Data draft overlay.
 *
 * Nothing here touches React, react-hook-form or the network, so every
 * transition in the plan's state machine is testable on its own. The provider
 * is a thin `useReducer` over this.
 *
 * Two counters, deliberately:
 *
 *   workingRevision  moves when the WORKING layer or a schema moves. It is what
 *                    a proposal's `baseRevision` is checked against.
 *   draftRevision    moves on anything visible, drafts included.
 *
 * Stamping one number for both is what makes an overlay impossible: staging a
 * proposal would move the number the NEXT proposal is validated against, and
 * every sibling suggestion generated in the same breath would stale-reject.
 */
import { sameAnswer } from './fieldValue';
import type {
  DraftCounts,
  FieldProposal,
  FormId,
  RiskDataDraftAction,
  RiskDataDraftState,
  StageRequest,
} from './types';
import type { FieldId, FieldValue } from '../../../../form-engine/types';

export function initialDraftState(
  accountId: string,
  sessionId: string
): RiskDataDraftState {
  return {
    accountId,
    sessionId,
    savedAnswers: {},
    workingAnswers: {},
    forms: {},
    workingRevision: 0,
    draftRevision: 0,
  };
}

/** The answer a field currently reads as, ignoring any draft on top of it. */
export function workingAnswer(
  state: RiskDataDraftState,
  fieldId: FieldId
): FieldValue | undefined {
  return state.workingAnswers[fieldId] ?? state.savedAnswers[fieldId];
}

/** The answer the form should RENDER — the draft wins while one is pending. */
export function visibleAnswer(
  state: RiskDataDraftState,
  formId: FormId,
  fieldId: FieldId
): FieldValue | undefined {
  const proposal = state.forms[formId]?.proposals[fieldId];
  return proposal ? proposal.proposedValue : workingAnswer(state, fieldId);
}

export function proposalsOf(
  state: RiskDataDraftState,
  formId: FormId
): readonly FieldProposal[] {
  return Object.values(state.forms[formId]?.proposals ?? {});
}

/** True while the selected form still holds something nobody has decided on. */
export function hasUnresolvedDrafts(
  state: RiskDataDraftState,
  formId: FormId
): boolean {
  return proposalsOf(state, formId).length > 0;
}

/** Banner arithmetic: "18 draft answers · 3 need review". */
/**
 * Membership test for a bulk action's scope.
 *
 * `undefined` means the whole form, which is what the banner's controls pass.
 * A list means one section.
 */
function scopeOf(
  fieldIds: readonly FieldId[] | undefined
): (fieldId: FieldId) => boolean {
  if (!fieldIds) {
    return () => true;
  }
  const set = new Set(fieldIds);
  return (fieldId) => set.has(fieldId);
}

const NO_DRAFTS: DraftCounts = { total: 0, pending: 0, needsReview: 0 };

export function draftCounts(
  state: RiskDataDraftState,
  formId: FormId,
  fieldIds?: readonly FieldId[]
): DraftCounts {
  const proposals = proposalsOf(state, formId);
  // Called once per section on every render of a form with hundreds of fields.
  // The common case is a form with no drafts at all, so bail before building
  // the scope set.
  if (proposals.length === 0) {
    return NO_DRAFTS;
  }

  const scope = scopeOf(fieldIds);
  let total = 0;
  let pending = 0;
  proposals.forEach((p) => {
    if (!scope(p.fieldId)) {
      return;
    }
    total += 1;
    if (p.status === 'pending') {
      pending += 1;
    }
  });
  return { total, pending, needsReview: total - pending };
}

/** Every form that still has drafts — drives the questionnaire badges. */
export function formsWithDrafts(
  state: RiskDataDraftState
): readonly { formId: FormId; formName: string; count: number }[] {
  return Object.values(state.forms)
    .map((form) => ({
      formId: form.formId,
      formName: form.formName,
      count: Object.keys(form.proposals).length,
    }))
    .filter((entry) => entry.count > 0);
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const bumpDraft = (state: RiskDataDraftState): RiskDataDraftState => ({
  ...state,
  draftRevision: state.draftRevision + 1,
});

const bumpBoth = (state: RiskDataDraftState): RiskDataDraftState => ({
  ...state,
  workingRevision: state.workingRevision + 1,
  draftRevision: state.draftRevision + 1,
});

function withProposals(
  state: RiskDataDraftState,
  formId: FormId,
  next: Record<FieldId, FieldProposal>
): RiskDataDraftState {
  const form = state.forms[formId];
  if (!form) {
    return state;
  }
  return {
    ...state,
    forms: { ...state.forms, [formId]: { ...form, proposals: next } },
  };
}

function dropProposal(
  state: RiskDataDraftState,
  formId: FormId,
  fieldId: FieldId
): RiskDataDraftState {
  const proposals = state.forms[formId]?.proposals;
  if (!proposals?.[fieldId]) {
    return state;
  }
  const next = { ...proposals };
  delete next[fieldId];
  return withProposals(state, formId, next);
}

/**
 * Re-check every OTHER form's pending proposal for a field whose working answer
 * just moved.
 *
 * Two questionnaires can expose the same field id, and the save path is
 * account-field scoped — so accepting in form A really does change what form B
 * is proposing against. Leaving B's proposal `pending` would let the user accept
 * a "before" value that stopped being true on another screen.
 */
function reconcileAgainstWorking(
  state: RiskDataDraftState,
  fieldId: FieldId,
  exceptFormId?: FormId
): RiskDataDraftState {
  const current = workingAnswer(state, fieldId);
  let next = state;

  for (const form of Object.values(state.forms)) {
    if (form.formId === exceptFormId) {
      continue;
    }
    const proposal = form.proposals[fieldId];
    if (!proposal || proposal.status === 'invalid') {
      continue;
    }
    if (sameAnswer(proposal.baselineValue, current)) {
      continue;
    }
    if (sameAnswer(proposal.proposedValue, current)) {
      // The other form's answer already matches what this draft proposes.
      // Nothing left to review, so the draft retires rather than nagging.
      next = dropProposal(next, form.formId, fieldId);
      continue;
    }
    next = withProposals(next, form.formId, {
      ...next.forms[form.formId].proposals,
      [fieldId]: {
        ...proposal,
        status: 'conflict',
        problem:
          'The answer changed elsewhere after this was drafted. Check it against the current answer before accepting.',
      },
    });
  }
  return next;
}

function stageInto(
  state: RiskDataDraftState,
  request: StageRequest
): RiskDataDraftState {
  const form = state.forms[request.formId];
  if (!form) {
    return state;
  }
  // Replay guard. A mount effect can run twice, and the store — not the
  // component — has to be the thing that remembers, or a remount restages.
  if (form.stagedChangeSetIds.includes(request.changeSetId)) {
    return state;
  }

  const proposals: Record<FieldId, FieldProposal> = { ...form.proposals };

  request.fields.forEach((field, index) => {
    const existing = proposals[field.fieldId];
    const baseline = workingAnswer(state, field.fieldId);
    const proposalId = `${request.changeSetId}:${index}`;

    if (field.unusable) {
      proposals[field.fieldId] = {
        proposalId,
        changeSetId: request.changeSetId,
        fieldId: field.fieldId,
        baselineValue: baseline,
        proposedValue: field.value,
        edited: false,
        status: 'invalid',
        problem: field.problem,
      };
      return;
    }

    // A value that already matches the working answer is not a change to
    // review. Staging it would show the user a draft with an identical before
    // and after and demand a decision about nothing.
    if (sameAnswer(baseline, field.value) && !existing) {
      return;
    }

    // The one case that must never be a silent overwrite: the user has typed
    // into this draft, and a newer extraction disagrees. Keep theirs, record
    // the rival, and make them choose.
    if (existing?.edited && !sameAnswer(existing.proposedValue, field.value)) {
      proposals[field.fieldId] = {
        ...existing,
        contestedValue: field.value,
        status: 'conflict',
        problem:
          'A newer extraction proposes a different answer than the one you edited.',
      };
      return;
    }

    proposals[field.fieldId] = {
      proposalId,
      changeSetId: request.changeSetId,
      fieldId: field.fieldId,
      baselineValue: baseline,
      proposedValue: field.value,
      edited: false,
      status: 'pending',
      // Carried on an ACCEPTABLE draft too: a partial multi-select match is
      // usable and still has something the user needs told, and dropping the
      // note here made accepting look like it took the whole answer.
      ...(field.problem ? { problem: field.problem } : {}),
    };
  });

  return bumpDraft({
    ...state,
    forms: {
      ...state.forms,
      [request.formId]: {
        ...form,
        formName: request.formName || form.formName,
        proposals,
        stagedChangeSetIds: [...form.stagedChangeSetIds, request.changeSetId],
      },
    },
  });
}

function acceptOne(
  state: RiskDataDraftState,
  formId: FormId,
  fieldId: FieldId
): RiskDataDraftState {
  const proposal = state.forms[formId]?.proposals[fieldId];
  // A conflict or an invalid value is not acceptable until it is resolved —
  // this is the guard that makes "Accept all" safe to offer.
  if (!proposal || proposal.status !== 'pending') {
    return state;
  }

  const accepted: RiskDataDraftState = {
    ...dropProposal(state, formId, fieldId),
    workingAnswers: {
      ...state.workingAnswers,
      [fieldId]: proposal.proposedValue,
    },
  };
  return bumpBoth(reconcileAgainstWorking(accepted, fieldId, formId));
}

export function riskDataDraftReducer(
  state: RiskDataDraftState,
  action: RiskDataDraftAction
): RiskDataDraftState {
  switch (action.type) {
    case 'RESET_ACCOUNT':
      return initialDraftState(
        action.payload.accountId,
        action.payload.sessionId
      );

    case 'REGISTER_FORM': {
      const existing = state.forms[action.payload.formId];
      // A fingerprint change means the questionnaire's schema moved under the
      // drafts. Anything staged against the old shape describes fields that may
      // no longer exist, so it is dropped rather than re-targeted by guesswork.
      const schemaMoved =
        existing != null &&
        existing.schemaFingerprint !== action.payload.schemaFingerprint;
      if (existing && !schemaMoved) {
        return existing.formName === action.payload.formName
          ? state
          : {
              ...state,
              forms: {
                ...state.forms,
                [action.payload.formId]: {
                  ...existing,
                  formName: action.payload.formName,
                },
              },
            };
      }
      return bumpBoth({
        ...state,
        forms: {
          ...state.forms,
          [action.payload.formId]: {
            ...action.payload,
            proposals: {},
            stagedChangeSetIds: existing?.stagedChangeSetIds ?? [],
          },
        },
      });
    }

    case 'REBASE': {
      const saved = action.payload.savedAnswers;
      // Three-way merge, not a replace. A refetch that landed while the user
      // was typing must not erase what they typed, and it must not erase a
      // pending proposal either — both are local work the server never saw.
      const working: Record<FieldId, FieldValue> = {};
      Object.keys(state.workingAnswers).forEach((fieldId) => {
        const local = state.workingAnswers[fieldId];
        const persisted = action.payload.fromSave
          ? (action.payload.savedFieldIds ?? Object.keys(saved)).includes(
              fieldId
            ) && sameAnswer(saved[fieldId], local)
          : sameAnswer(saved[fieldId], local);
        if (!persisted) {
          working[fieldId] = local;
        }
      });
      return bumpBoth({
        ...state,
        savedAnswers: saved,
        workingAnswers: working,
      });
    }

    case 'EDIT_WORKING': {
      const { fieldId, value } = action.payload;
      if (sameAnswer(workingAnswer(state, fieldId), value)) {
        return state;
      }
      const edited: RiskDataDraftState = {
        ...state,
        workingAnswers: { ...state.workingAnswers, [fieldId]: value },
      };
      return bumpBoth(reconcileAgainstWorking(edited, fieldId));
    }

    case 'STAGE':
      return stageInto(state, action.payload);

    case 'EDIT_PROPOSAL': {
      const { formId, fieldId, value } = action.payload;
      const proposal = state.forms[formId]?.proposals[fieldId];
      if (!proposal) {
        return state;
      }
      // Editing a draft keeps it a draft. Folding the edit into the working
      // layer would quietly accept an AI answer the user was only adjusting.
      return bumpDraft(
        withProposals(state, formId, {
          ...state.forms[formId].proposals,
          [fieldId]: {
            ...proposal,
            proposedValue: value,
            edited: true,
            ...(proposal.status === 'invalid'
              ? { status: 'pending' as const, problem: undefined }
              : {}),
          },
        })
      );
    }

    case 'RESOLVE_CONFLICT': {
      const { formId, fieldId, keep } = action.payload;
      const proposal = state.forms[formId]?.proposals[fieldId];
      if (!proposal || proposal.status !== 'conflict') {
        return state;
      }
      const chosen =
        keep === 'contested' && proposal.contestedValue !== undefined
          ? proposal.contestedValue
          : proposal.proposedValue;
      return bumpDraft(
        withProposals(state, formId, {
          ...state.forms[formId].proposals,
          [fieldId]: {
            ...proposal,
            proposedValue: chosen,
            contestedValue: undefined,
            // Rebased onto the answer as it stands NOW, so the before/after the
            // user reviews is the one they will actually get.
            baselineValue: workingAnswer(state, fieldId),
            status: 'pending',
            problem: undefined,
          },
        })
      );
    }

    case 'ACCEPT':
      return acceptOne(state, action.payload.formId, action.payload.fieldId);

    case 'REJECT': {
      // Restores the WORKING answer, not the server baseline: a manual unsaved
      // edit can predate the extraction, and reverting past it would delete
      // work the user did themselves.
      const next = dropProposal(
        state,
        action.payload.formId,
        action.payload.fieldId
      );
      return next === state ? state : bumpDraft(next);
    }

    case 'ACCEPT_ALL': {
      const { formId, fieldIds } = action.payload;
      const scope = scopeOf(fieldIds);
      return proposalsOf(state, formId)
        .filter((p) => p.status === 'pending' && scope(p.fieldId))
        .reduce((acc, p) => acceptOne(acc, formId, p.fieldId), state);
    }

    case 'REJECT_ALL': {
      const { formId, fieldIds } = action.payload;
      const form = state.forms[formId];
      if (!form) {
        return state;
      }
      if (!fieldIds) {
        return bumpDraft(withProposals(state, formId, {}));
      }
      // Rebuilt by exclusion rather than deleted in place, so a scoped reject
      // cannot disturb the drafts outside it.
      const scope = scopeOf(fieldIds);
      const kept = Object.fromEntries(
        Object.entries(form.proposals).filter(([fieldId]) => !scope(fieldId))
      );
      if (Object.keys(kept).length === Object.keys(form.proposals).length) {
        return state;
      }
      return bumpDraft(withProposals(state, formId, kept));
    }

    default:
      return state;
  }
}
